import React, { useState } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import tw from 'twin.macro'
import { CgClose } from 'react-icons/cg'
import { FiPaperclip } from 'react-icons/fi'

import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { Button } from 'common/components/Button/Button'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { textareaBase } from 'common/components/Textarea/Textarea'

import { useCreateCommunityReplyMutation, useEditCommunityReplyMutation } from '../communityApi'
import { REPLY_MAX_LENGTH } from '../communityConstants'
import { buildReplyEditFields, getReplyAttachment } from '../postButtonUtils'
import useCoachAuthor from '../useCoachAuthor'
import PostAttachmentCard, { usePostAttachment } from './PostAttachmentCard'
import ReplyAttachmentForm from './ReplyAttachmentForm'

// The reply box under a thread. Given `reply`, it edits that reply instead: it opens on
// the reply's text and attachment, and the attachment is removed with the x and chosen
// again exactly as when replying for the first time. `onDone` closes the editor.
export default function ReplyComposer({ post, coachOrgId, userId, reply, onDone }) {
  const isEditing = Boolean(reply)
  const { createAlert } = useAlert()
  const author = useCoachAuthor({ coachOrgId, userId })
  const [createCommunityReply] = useCreateCommunityReplyMutation()
  const [editCommunityReply] = useEditCommunityReplyMutation()

  const [text, setText] = useState(reply?.text || '')
  const [sending, setSending] = useState(false)
  const [attachment, setAttachment] = useState(() => getReplyAttachment(reply))
  const [attachDialogOpen, setAttachDialogOpen] = useState(false)
  const trimmed = text.trim()

  const handleSend = async () => {
    if (!trimmed || sending) return
    setSending(true)
    const result = isEditing
      ? await editCommunityReply({
          coachOrgId,
          postId: post.id,
          replyId: reply.id,
          fields: buildReplyEditFields({ text: trimmed, attachmentFields: attachment?.fields }),
        })
      : await createCommunityReply({
          coachOrgId,
          postId: post.id,
          postAuthorId: post.authorId,
          userId,
          author,
          text: trimmed,
          richFields: attachment?.fields,
        })
    setSending(false)
    if (result?.error || result?.data?.error) {
      createAlert({
        text: isEditing ? 'Could not save reply. Please try again.' : 'Could not send reply. Please try again.',
        type: 'danger',
      })
      return
    }
    if (isEditing) {
      onDone?.()
      return
    }
    setText('')
    setAttachment(null)
  }

  return (
    <div className='mt-3'>
      <Dialog open={attachDialogOpen} setOpen={setAttachDialogOpen}>
        <div className='rounded-xl bg-white ring-1 ring-gray-300 focus-within:ring-tGreen transition-shadow'>
          <TextareaAutosize
            minRows={2}
            maxRows={8}
            css={[textareaBase, tw`h-auto py-3 bg-transparent ring-0 focus:ring-0`]}
            placeholder='Write a reply...'
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={REPLY_MAX_LENGTH}
            aria-label={isEditing ? 'Edit reply' : 'Write a reply'}
            autoFocus={isEditing}
          />
          {attachment ? (
            <AttachmentPreview
              fields={attachment.fields}
              coachOrgId={coachOrgId}
              onEdit={() => setAttachDialogOpen(true)}
              onRemove={() => setAttachment(null)}
            />
          ) : (
            <div className='px-2 pb-2'>
              <DialogTrigger className='inline-flex items-center rounded-lg px-2 py-1.5 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-tBlack transition-colors'>
                <FiPaperclip className='w-4 h-4 mr-1.5' />
                Add content
              </DialogTrigger>
            </div>
          )}
        </div>
        <DialogContent header={attachment ? 'Change attachment' : 'Add content'}>
          <FormRefsControlProvider>
            <ReplyAttachmentForm
              postId={isEditing ? `${post.id}-${reply.id}` : post.id}
              coachOrgId={coachOrgId}
              initialAttachment={attachment?.draft}
              onAttach={setAttachment}
            />
          </FormRefsControlProvider>
        </DialogContent>
      </Dialog>
      <div className='flex justify-end mt-2'>
        {isEditing && (
          <Button size='md' variant='secondary' css={tw`mr-2`} onClick={() => onDone?.()} disabled={sending}>
            Cancel
          </Button>
        )}
        <Button size='md' onClick={handleSend} loading={sending} disabled={!trimmed || sending}>
          {isEditing ? 'Save' : 'Reply'}
        </Button>
      </div>
    </div>
  )
}

function AttachmentPreview({ fields, coachOrgId, onEdit, onRemove }) {
  const card = usePostAttachment(fields, coachOrgId)

  return (
    <div className='px-4 pb-3'>
      <div className='relative inline-block max-w-full'>
        <button
          type='button'
          onClick={onEdit}
          aria-label='Change attachment'
          className='block max-w-full text-left transition-opacity hover:opacity-80'
        >
          <PostAttachmentCard attachment={card} />
        </button>
        <button
          type='button'
          onClick={onRemove}
          aria-label='Remove attachment'
          className='absolute top-1.5 -right-2 rounded-full bg-white p-1 text-gray-500 shadow ring-1 ring-gray-200 hover:text-tRed transition-colors'
        >
          <CgClose className='w-3 h-3' />
        </button>
      </div>
    </div>
  )
}
